import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@mantine/core";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import "../styles/LandingStyles.css";

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_PHONE_LENGTH = 15;
const MAX_NAME_LENGTH = 20;
const NAME_REGEX = /[^a-zA-Z\s'-]/g;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [emailError, setEmailError] = useState("");

  const validateEmail = (emailValue) => EMAIL_REGEX.test(emailValue);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (e) => {
    const value = e.target.value
      .replace(NAME_REGEX, "")
      .slice(0, MAX_NAME_LENGTH);
    updateFormData(e.target.name, value);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);
    updateFormData("phone", value);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    updateFormData("email", value);
    if (emailError) setEmailError("");
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const resetForm = () => {
    setFormData({ firstName: "", lastName: "", email: "", phone: "" });
    setEmailError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    const message = e.target.message.value;
    const mailtoLink = `mailto:admin@bammbuu.com?subject=${encodeURIComponent(
      "Contact Form Submission",
    )}&body=${encodeURIComponent(
      `Name: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nMessage: ${message}`,
    )}`;

    window.location.href = mailtoLink;
    e.target.reset();
    resetForm();
  };
  return (
    <>
      <div className="overflow-hidden font-urbanist">
        {/* Contact Us Section */}
        <div className="bg-[#E6FDE9] pb-32">
          <div className="flex flex-col p-12 pb-0">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link
                to="/"
                className="text-4xl font-bold text-green-600 md:text-7xl"
              >
                <img
                  alt="bambuu"
                  src="/svgs/logobammbuu.svg"
                  className="h-auto w-40 md:w-auto"
                />
              </Link>

              <div className="items-center space-x-4">
                <Link className="text-base font-medium hover:text-green-600">
                  <Button
                    className="border border-[#042F0C] text-black"
                    size="md"
                    variant="filled"
                    color="#14b82c"
                    radius="xl"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col items-center justify-center space-y-8 px-4 lg:mt-20 lg:space-y-16 lg:px-0">
            <div className="mb-6 flex flex-col items-center justify-center space-y-4">
              <h1 className="text-center text-lg font-semibold text-[#14B82C] lg:text-xl">
                Contact Us
              </h1>
              <h1 className="text-center text-xl font-bold text-[#042F0C] lg:text-5xl">
                We’d love to hear from you
              </h1>
              <h1 className="text-center text-lg text-[#454545] lg:text-2xl">
                Please fill out this form.
              </h1>
            </div>
          </div>
        </div>
        {/* Contact Form Section */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mt-20 max-w-xl p-8"
        >
          <form onSubmit={handleSubmit}>
            <div className="mb-6 flex gap-4">
              <div className="flex-1">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleNameChange}
                  placeholder="enter your first name"
                  className="w-full rounded-full border border-gray-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-green-500"
                  maxLength={MAX_NAME_LENGTH}
                  required
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleNameChange}
                  placeholder="enter your last name"
                  className="w-full rounded-full border border-gray-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-green-500"
                  maxLength={MAX_NAME_LENGTH}
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder="enter your email"
                className={`w-full rounded-full border px-4 py-3 focus:outline-none focus:ring-1 ${
                  emailError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                }`}
                required
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-500">{emailError}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="enter your phone number"
                pattern="[0-9]*"
                maxLength={MAX_PHONE_LENGTH}
                className="w-full rounded-full border border-gray-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-green-500"
                required
              />
            </div>

            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                name="message"
                placeholder="Leave us a message..."
                rows="4"
                className="w-full rounded-3xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-green-500"
                required
              />
            </div>

            <div className="mb-8 flex items-center gap-2">
              <input
                type="checkbox"
                id="privacy"
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                required
              />
              <label
                htmlFor="privacy"
                className="text-base font-normal text-gray-600"
              >
                You agree to our friendly{" "}
                <Link to="/privacy-policy" className="underline">
                  privacy policy
                </Link>
              </label>
            </div>

            <button
              type="submit"
              className="w-full rounded-full border border-[#042F0C] bg-[#14B82C] px-4 py-3 text-black transition-colors hover:cursor-pointer"
            >
              Send message
            </button>
          </form>
        </motion.div>
      </div>
      <Footer />
    </>
  );
};

export default ContactUs;
